import type { ReactNode } from "react";

interface Props {
  note?: string | null;
  /** Label of the status this alert/assignment was in right before the latest change. */
  fromLabel?: string | null;
  /** Label of the status it was changed to (the current status this chip represents). */
  toLabel: string;
  children: ReactNode;
  className?: string;
}

/** Wraps a status badge/button with a hover popover revealing the status transition
 * (e.g. "New → Reviewing") and the note typed at that change — same visual pattern
 * as RemoteScopeChip's hover popover. Renders children unchanged (no popover) when
 * there's no note. */
export default function StatusNotePopover({ note, fromLabel, toLabel, children, className }: Props) {
  if (!note) return <>{children}</>;

  return (
    <span className={`group relative inline-flex shrink-0 ${className ?? ""}`}>
      {children}

      {/* pt-2 (not mt-2) keeps the gap inside this element's own hoverable box, so moving
          the mouse from the badge down into the popover never crosses a dead zone that
          would drop group-hover and hide the popover before the cursor arrives. */}
      <div className="hidden group-hover:block absolute z-30 top-full right-0 w-64 pt-2">
        <div className="bg-surface-muted border border-border rounded-xl shadow-2xl p-3 text-left">
          <p className="text-[11px] font-semibold text-primary font-inter mb-1.5">
            {fromLabel ? `${fromLabel} → ${toLabel}` : toLabel}
          </p>
          <p className="text-[11px] text-muted-foreground font-inter leading-snug">{note}</p>
        </div>
      </div>
    </span>
  );
}
